import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoadingOverlay, MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "./globals.css";
import { theme } from "./theme";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { OwnerScopeProvider } from "./context/OwnerScopeContext";
import { ConversationDrawerProvider } from "./context/ConversationDrawerContext";
import { HeaderSlotProvider } from "./context/HeaderSlotContext";
import { FilterProvider } from "./context/FilterContext";
import { AdminLayout } from "./components/AdminLayout";
import { LoginPage } from "./pages/LoginPage";

const OwnerDashboardPage = lazy(() => import("./pages/OwnerDashboardPage"));
const OwnerJobsPage = lazy(() => import("./pages/OwnerJobsPage"));
const OwnerChargersPage = lazy(() => import("./pages/OwnerChargersPage"));
const OwnerInsightsPage = lazy(() => import("./pages/OwnerInsightsPage"));
const OwnerTeamPage = lazy(() => import("./pages/OwnerTeamPage"));
const ShopListPage = lazy(() => import("./pages/ShopListPage").then((m) => ({ default: m.ShopListPage })));
const ShopDetailPage = lazy(() => import("./pages/ShopDetailPage").then((m) => ({ default: m.ShopDetailPage })));
const ShopConversationPage = lazy(() => import("./pages/ShopConversationPage").then((m) => ({ default: m.ShopConversationPage })));
const UserListPage = lazy(() => import("./pages/UserListPage").then((m) => ({ default: m.UserListPage })));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const LegalDocsAuditPage = lazy(() => import("./pages/LegalDocsAuditPage").then((m) => ({ default: m.LegalDocsAuditPage })));
const TemplateListPage = lazy(() => import("./pages/TemplateListPage").then((m) => ({ default: m.TemplateListPage })));
const TemplateDetailPage = lazy(() => import("./pages/TemplateDetailPage").then((m) => ({ default: m.TemplateDetailPage })));
const ConversationListPage = lazy(() => import("./pages/ConversationListPage").then((m) => ({ default: m.ConversationListPage })));
const EmailTemplateListPage = lazy(() => import("./pages/EmailTemplateListPage").then((m) => ({ default: m.EmailTemplateListPage })));

export default function AdminApp() {
  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <div className="haist-admin">
        <AdminAuthProvider>
          <OwnerScopeProvider>
            <HeaderSlotProvider>
            <FilterProvider>
            <ConversationDrawerProvider>
              <Suspense fallback={<LoadingOverlay visible loaderProps={{ type: "bars" }} />}>
                <Routes>
                  <Route path="login" element={<LoginPage />} />
                  <Route element={<AdminLayout />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<OwnerDashboardPage />} />
                    <Route path="conversations" element={<OwnerJobsPage />} />
                    <Route path="chargers" element={<OwnerChargersPage />} />
                    <Route path="jobs" element={<Navigate to="/admin/conversations" replace />} />
                    <Route path="insights" element={<OwnerInsightsPage />} />
                    <Route path="team" element={<OwnerTeamPage />} />
                    <Route path="shops" element={<ShopListPage />} />
                    <Route path="shops/:id" element={<ShopDetailPage />} />
                    <Route path="shops/:id/conversations/:conversationId" element={<ShopConversationPage />} />
                    <Route path="legal-documents" element={<LegalDocsAuditPage />} />
                    <Route path="users" element={<UserListPage />} />
                    <Route path="users/:id" element={<UserDetailPage />} />
                    <Route path="templates" element={<TemplateListPage />} />
                    <Route path="templates/:id" element={<TemplateDetailPage />} />
                    <Route path="email-templates" element={<EmailTemplateListPage />} />
                    <Route path="_legacy/conversations" element={<ConversationListPage />} />
                    <Route path="_legacy/conversations/:id" element={<ConversationListPage />} />
                  </Route>
                </Routes>
              </Suspense>
            </ConversationDrawerProvider>
            </FilterProvider>
            </HeaderSlotProvider>
          </OwnerScopeProvider>
        </AdminAuthProvider>
      </div>
    </MantineProvider>
  );
}
